import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import ClientSearch from "./client_search";
import Modal from "./modal";
import { labels, requestKey, time } from "./api";

const titles = {
  create: "Add task or alert",
  edit: "Edit wording or date",
  review: "Monthly alert review",
  archived: "Archive TED follow-up",
  archive: "Archive TED follow-up",
  reactivate: "Reactivate TED follow-up",
  acknowledged: "Acknowledge",
  completed: "Mark complete",
  care_performed: "Record care performed",
  not_completed: "Seen but not completed",
  closed: "Close item",
  note: "Add note"
};

export default function ActionForm({ action, board, disabled, onSave, onClose }) {
  const { operation, item } = action;
  const [client, setClient] = useState(action.client || null);
  const [decision, setDecision] = useState("keep");
  const [resolveIds, setResolveIds] = useState([]);
  const [disposition, setDisposition] = useState("preserve");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const retry = useRef(null);
  const isCreate = operation === "create";
  const needsEncounter = ["acknowledged", "care_performed", "not_completed", "completed"].includes(
    operation
  );
  const encounters = board.sightings
    .filter((sighting) => sighting.client_id === client?.id)
    .slice()
    .reverse();
  const openItems = board.items.filter((row) => row.client_id === client?.id);
  const isArchive = operation === "archive";
  const isReview = operation === "review";
  const editFields = isCreate || operation === "edit" || (isReview && decision === "edit");
  const reasonRequired =
    ["closed", "archive", "reactivate", "note"].includes(operation) ||
    (isReview && decision === "close");
  const acknowledged = (sightingId) =>
    operation === "acknowledged" &&
    board.acknowledgments.some(
      (ack) =>
        ack.item_id === item.id && ack.sighting_id === sightingId && ack.revision === item.revision
    );
  const defaultEncounter =
    action.encounterId && !acknowledged(action.encounterId) ? String(action.encounterId) : "";
  const close = () => {
    if (!saving) onClose();
  };
  const submit = async (event) => {
    event.preventDefault();
    if (saving || disabled) return;
    if (!client) {
      setError("Choose a client first.");
      return;
    }
    if (isCreate && client.follow_up === "archived") {
      setError("Reactivate this client's TED follow-up before adding new care.");
      return;
    }
    if (isArchive && disposition === "resolve" && !resolveIds.length) {
      setError("Select at least one item to resolve, or choose preserve all.");
      return;
    }
    const fields = Object.fromEntries(new FormData(event.currentTarget));
    const payload = {
      ...fields,
      operation,
      client_id: client.id,
      ...(item ? { item_id: item.id, lock_version: item.lock_version } : {}),
      ...(["archive", "reactivate"].includes(operation)
        ? { enrollment_version: client.enrollment_version }
        : {}),
      ...(isArchive ? { resolve_ids: disposition === "preserve" ? [] : resolveIds } : {})
    };
    const signature = JSON.stringify(payload);
    if (retry.current?.signature !== signature) retry.current = { signature, key: requestKey() };
    setSaving(true);
    setError("");
    try {
      await onSave({ ...payload, request_key: retry.current.key });
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };
  return (
    <Modal title={titles[operation] || labels[operation]} onClose={close}>
      <form onSubmit={submit}>
        <div className="cb-dialog__body ted-form-fields">
          {item && (
            <div className="cb-banner cb-banner--info">
              <div>
                <strong>{client.name}</strong>
                <p>{item.description}</p>
              </div>
            </div>
          )}
          {isCreate && (
            <div>
              <label>Client</label>
              {client ? (
                <p>
                  <strong>{client.name}</strong> · ID {client.id} · {client.residence}{" "}
                  <button type="button" className="cb-link" onClick={() => setClient(null)}>
                    Change
                  </button>
                </p>
              ) : (
                <ClientSearch onSelect={setClient} purpose="create" disabled={saving || disabled} />
              )}
              {client?.follow_up === "archived" && (
                <p role="alert">
                  This follow-up is archived. Close this dialog and reactivate it from client
                  details first.
                </p>
              )}
            </div>
          )}
          {isCreate && (
            <div className="ted-form-row">
              <label>
                Type
                <select name="kind">
                  <option value="task">Task</option>
                  <option value="alert">Alert</option>
                </select>
              </label>
              <label>
                Persistence
                <select name="persistence">
                  <option value="one_time">One-time</option>
                  <option value="persistent">Persistent</option>
                </select>
              </label>
            </div>
          )}
          {isReview && (
            <label>
              Review decision
              <select
                name="decision"
                value={decision}
                onChange={(e) => setDecision(e.target.value)}
              >
                <option value="keep">Keep as is</option>
                <option value="edit">Edit and keep</option>
                <option value="close">Close alert</option>
              </select>
            </label>
          )}
          {editFields && (
            <>
              <label>
                Description
                <textarea
                  name="description"
                  defaultValue={item?.description || ""}
                  required
                  maxLength={4000}
                  rows={3}
                />
              </label>
              <label>
                Target date (optional)
                <input type="date" name="target_date" defaultValue={item?.target_date || ""} />
              </label>
              <p className="cb-muted">
                Target dates are reminders. The item appears on the board immediately.
              </p>
            </>
          )}
          {operation === "edit" && (
            <p className="cb-muted">
              Type and persistence stay fixed. Earlier wording remains in the audit history.
            </p>
          )}
          {isReview && (
            <p className="cb-muted">
              Keeping this alert schedules its next review one calendar month from now. No sighting
              will be recorded.
            </p>
          )}
          {needsEncounter && (
            <label>
              Sighting
              <select name="encounter" required defaultValue={defaultEncounter}>
                <option value="" disabled>
                  Choose encounter context
                </option>
                {encounters.map((sighting) => (
                  <option
                    key={sighting.id}
                    value={sighting.id}
                    disabled={acknowledged(sighting.id)}
                  >
                    {time(sighting.seen_at, board.time_zone)} · {sighting.actor}
                    {acknowledged(sighting.id) ? " · already acknowledged" : ""}
                  </option>
                ))}
                <option value="new">I am seeing this client now — record a new sighting</option>
                {operation === "completed" && <option value="none">Not part of a sighting</option>}
              </select>
            </label>
          )}
          {operation === "acknowledged" && (
            <p className="cb-muted">
              Acknowledgment means you read the instruction. Record care performed separately.
            </p>
          )}
          {isArchive && (
            <>
              <p>
                <strong>{client.name}</strong> · Archive TED follow-up only. The client profile and
                history stay available.
              </p>
              <label className="ted-choice">
                <input
                  type="radio"
                  checked={disposition === "preserve"}
                  onChange={() => setDisposition("preserve")}
                />
                Preserve all open items for a possible return
              </label>
              <label className="ted-choice">
                <input
                  type="radio"
                  checked={disposition === "resolve"}
                  onChange={() => setDisposition("resolve")}
                />
                Resolve selected items now
              </label>
              {disposition === "resolve" && (
                <div className="ted-form-fields">
                  <p>
                    Unselected items are preserved. One-time work is withdrawn, never marked
                    completed.
                  </p>
                  <div>
                    <button
                      type="button"
                      className="cb-link"
                      onClick={() => setResolveIds(openItems.map((row) => row.id))}
                    >
                      Select all
                    </button>{" "}
                    ·{" "}
                    <button type="button" className="cb-link" onClick={() => setResolveIds([])}>
                      Clear selection
                    </button>
                  </div>
                  {openItems.map((row) => (
                    <label className="ted-choice" key={row.id}>
                      <input
                        type="checkbox"
                        checked={resolveIds.includes(row.id)}
                        onChange={(e) =>
                          setResolveIds(
                            e.target.checked
                              ? [...resolveIds, row.id]
                              : resolveIds.filter((id) => id !== row.id)
                          )
                        }
                      />
                      <span>
                        {row.description}
                        <br />
                        <span className="cb-muted">
                          {row.persistence === "persistent"
                            ? "Will be closed"
                            : "Will be withdrawn, not completed"}
                        </span>
                      </span>
                    </label>
                  ))}
                  <p role="status">
                    {resolveIds.length} item(s) will be resolved;{" "}
                    {openItems.length - resolveIds.length} will be preserved.
                  </p>
                </div>
              )}
            </>
          )}
          {operation === "reactivate" && (
            <p>
              Restore {client.name}’s preserved open items. Completed, closed and withdrawn items
              stay resolved.
            </p>
          )}
          {!isCreate && operation !== "edit" && (
            <label>
              {reasonRequired ? "Reason / note" : "Note (optional)"}
              <textarea name="note" rows={3} required={reasonRequired} maxLength={4000} />
            </label>
          )}
        </div>
        {(error || disabled) && (
          <div className="ted-form-feedback">
            {error && (
              <p role="alert" className="ted-error">
                {error}
              </p>
            )}
            {disabled && <p role="alert">Updates are unavailable. Reconnect before saving.</p>}
          </div>
        )}
        <div className="cb-dialog__footer">
          <button
            type="button"
            className="ccw-button ccw-button--quiet"
            disabled={saving}
            onClick={close}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="ccw-button ccw-button--primary"
            disabled={saving || disabled || (isCreate && client?.follow_up === "archived")}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
ActionForm.propTypes = {
  action: PropTypes.object.isRequired,
  board: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};
